import dayjs from 'dayjs';
import weekYear from 'dayjs/plugin/weekYear';
import { isNil } from 'lodash-es';
import * as echarts from 'echarts';
import { sum } from '../../utils';
import { LossDataPostItemResponse } from '../LossDuringTransAndDisProcess';
import { V2EnergyCompareGetMediumIndicatorLossDataLossDetailPostResponse } from '@maxtropy/device-customer-apis-v2';
import { MediumIndicatorDisplayUnit } from '@/api/energyMedium';
import { TimeActive } from '../LossDuringTransAndDisProcess/type';

dayjs.extend(weekYear);

export enum Status {
  OVER = 1, // 超限
  NORMAL = 2, // 正常
}
export const StatusDisplay = {
  [Status.OVER]: '超限',
  [Status.NORMAL]: '正常',
};

const xAxis = {
  axisTick: {
    show: false,
  },
  splitLine: {
    show: false,
  },
};

const parseChartTime = (value: string | number) => {
  if (typeof value === 'number') {
    return value < 100000000000 ? dayjs.unix(value) : dayjs(value);
  }
  const normalizedValue = value.trim();
  if (/^\d+$/.test(normalizedValue)) {
    const timestamp = Number(normalizedValue);
    return normalizedValue.length <= 10 ? dayjs.unix(timestamp) : dayjs(timestamp);
  }
  return dayjs(normalizedValue);
};

const formatChartTime = (value: string | number, activeKey?: TimeActive, showFullDate = false) => {
  const date = parseChartTime(value);
  if (activeKey === TimeActive.HOUR) {
    return date.format(showFullDate ? 'YYYY-MM-DD HH:mm:ss' : 'MM-DD HH:mm:ss');
  }
  if (activeKey === TimeActive.THIS_WEEK) {
    return `${date.weekYear()}-${date.week()}周`;
  }
  if (activeKey === TimeActive.THIS_MONTH) {
    return date.format('YYYY-MM月');
  }
  return date.format(showFullDate ? 'YYYY-MM-DD' : 'MM-DD');
};

export const getNormalAxis = (activeKey?: TimeActive, periodAxisData?: Array<string | number>) => {
  let formatter;
  if (activeKey === TimeActive.CUSTOM) {
    formatter = function (e: string | number) {
      return formatChartTime(e, activeKey);
    };
  } else if (activeKey === TimeActive.HOUR) {
    formatter = function (e: string | number) {
      return formatChartTime(e, activeKey);
    };
  } else if (activeKey === TimeActive.THIS_WEEK) {
    formatter = function (e: string | number) {
      return formatChartTime(e, activeKey);
    };
  } else if (activeKey === TimeActive.THIS_MONTH) {
    formatter = function (e: string | number) {
      return formatChartTime(e, activeKey);
    };
  } else {
    formatter = function (e: string | number) {
      return formatChartTime(e, activeKey);
    };
  }
  const isPeriodQuery = activeKey === TimeActive.THIS_WEEK || activeKey === TimeActive.THIS_MONTH;
  return {
    ...xAxis,
    offset: 18,
    type: isPeriodQuery ? 'category' : 'time',
    data: isPeriodQuery ? periodAxisData : undefined,
    axisLabel: {
      formatter,
    },
  };
};

export const getTooltip = (
  { unit = '', energyMediumName = '' }: { unit?: string; energyMediumName?: string },
  activeKey?: TimeActive
) => ({
  formatter(items: any) {
    const { axisValue } = items[0];
    // 根据选择的颗粒度，展示不同的tooltip
    let str: string = formatChartTime(axisValue, activeKey, true);
    const rowStyle = 'display:flex; justify-content:space-between; align-items: flex-end; width:250px;';
    const leftTextStyle = 'flex:1;word-break:break-word; white-space:normal;padding-right: 16px;';
    items.forEach((item: any) => {
      const { seriesName, data, marker } = item;
      if (seriesName.includes(StatusDisplay[Status.OVER]) || seriesName.includes(StatusDisplay[Status.NORMAL])) {
        if (!isNil(data.value[1])) {
          const value = !isNil(data.value[1]) ? `${data.value[1].toFixed(2)}${unit}` : '--';
          str += `
      <div style="${rowStyle}">
        <span style="${leftTextStyle}">${marker}${seriesName}</span>
        <span style="white-space:nowrap;">${value}</span>
      </div>`;
        }
      } else {
        const value = !isNil(data[1]) ? `${data[1].toFixed(2)}${unit}` : '--';
        str += `
      <div style="${rowStyle}">
        <span style="${leftTextStyle}">${marker}${seriesName}--${energyMediumName}</span>
        <span style="white-space:nowrap;">${value}</span>
      </div>`;
        (data[data.length - 1] ?? []).forEach((i: any) => {
          const value = !isNil(i.sum) ? `${i.sum.toFixed(2)}${unit}` : '--';
          str += `
        <div style="${rowStyle}">
          <span style="${leftTextStyle} padding-left:16px;">${i.entryExitName}</span>
          <span style="white-space:nowrap;">${value}</span>
        </div>`;
        });
      }
    });
    return str;
  },
});

// 获取图例
export const getLegend = () => {
  return {
    show: true,
    right: 20,
    top: 10,
    data: [
      {
        name: `输入`,
        icon: 'rect',
      },
      {
        name: `输出`,
        icon: 'rect',
      },
      {
        name: `损耗（超限）`,
        icon: 'rect',
      },
      {
        name: `损耗（正常）`,
        icon: 'rect',
      },
    ],
  };
};

export const getChartOption = (
  unit: string,
  energyMediumName?: string,
  activeKey?: TimeActive,
  periodAxisData?: Array<string | number>
) => {
  const colors = ['#1890ff', '#4fc7ec'];
  return {
    grid: {
      bottom: 60,
    },
    dataZoom: {
      type: 'slider',
      bottom: 15,
      height: 20,
    },

    color: colors,
    tooltip: getTooltip(
      {
        unit,
        energyMediumName,
      },
      activeKey
    ),
    xAxis: getNormalAxis(activeKey, periodAxisData),
    yAxis: {
      type: 'value',
      name: `${unit}`,
      // nameTextStyle: {
      //   fontSize: 14,
      //   align: 'right',
      // },
    },
  };
};

const lossMarkArea = (
  processLossViewVoToIns: LossDataPostItemResponse['processLossViewVoToIns'],
  activeKey?: TimeActive
) => {
  const isPeriodQuery = activeKey === TimeActive.THIS_WEEK || activeKey === TimeActive.THIS_MONTH;
  return processLossViewVoToIns?.map((sl, index) => {
    return [
      {
        xAxis: isPeriodQuery
          ? index - 0.4
          : dayjs(sl.ts as string)
              .startOf('day')
              .subtract(12, 'hour')
              .valueOf(),
        label: {
          show: false,
          width: 120 - 24,
          height: 60 - 16,
          backgroundColor: 'rgba(0,0,0,0.8)',
          padding: [8, 12],
          zIndex: 99,
          z: 99,
          formatter: [
            `{status|${sl.lossDataVo?.ifExceed ? StatusDisplay[Status.OVER] : StatusDisplay[Status.NORMAL]}}状态`,
            `{time|${formatChartTime(sl.ts as string, activeKey, true)}}`,
          ].join('\n\n'),

          rich: {
            status: {
              color: 'rgba(255,255,255,0.65)',
              lineHeight: 17,
            },
            time: {
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 17,
              fonWeight: 500,
            },
          },
        },
        emphasis: {
          label: {
            show: true,
            position: ['0%', '-600%'],
            zIndex: 99,
          },
        },
        blur: {
          label: {
            show: false,
          },
        },
        itemStyle: {
          color: sl.lossDataVo?.ifExceed ? '#91383A' : '#2A6948',
        },
        y: '84%',
      },
      {
        xAxis: isPeriodQuery
          ? index + 0.4
          : dayjs(sl.ts as string)
              .endOf('day')
              .subtract(12, 'hour')
              .valueOf(),
        y: '86%',
      },
    ];
  });
};

const biasItemSty = (fullColor: string, biasColor: string) => {
  return {
    color: fullColor,
    decal: {
      color: biasColor,
      dashArrayX: [1, 0],
      dashArrayY: [4, 3],
      rotation: -Math.PI / 4,
    },
  };
};

export const getEnergyMediumLossChartOption = (
  processLossViewVoToIns?: LossDataPostItemResponse['processLossViewVoToIns'],
  energyMediumIndicatorId?: number,
  mediumIndicatorDisplayUnitData?: MediumIndicatorDisplayUnit,
  energyMediumName?: string,
  activeKey?: TimeActive
) => {
  if (processLossViewVoToIns && energyMediumIndicatorId && mediumIndicatorDisplayUnitData) {
    const unit = mediumIndicatorDisplayUnitData?.find(
      i => i.energyMediumIndicatorId === energyMediumIndicatorId
    )?.displayPhysicalUnitGeneralName;
    const periodAxisData = processLossViewVoToIns.map(item => item.ts).filter((ts): ts is string => !isNil(ts));
    const option = unit && getChartOption(unit, energyMediumName, activeKey, periodAxisData);
    // const colors = ['#00ADFF', '#00ADFF'];
    return {
      ...option,
      legend: getLegend(),
      series: [
        {
          name: '输入',
          type: 'bar',
          // stack: 'Ad',
          color: '#2D8DFF',
          emphasis: {
            focus: 'series',
          },
          z: 0,
          data: processLossViewVoToIns.map(i => [
            i.ts,
            !isNaN(sum(i.entryDataList?.map(m => m.sum))) ? sum(i.entryDataList?.map(m => m.sum)) : null,
            i.entryDataList,
          ]),
          markArea: {
            data: lossMarkArea(processLossViewVoToIns, activeKey),
          },
        },
        {
          name: '输出',
          type: 'bar',
          stack: 'Ad',
          color: '#16DD8E',
          emphasis: {
            focus: 'series',
          },
          z: 0,
          data: processLossViewVoToIns.map(i => [
            i.ts,
            !isNaN(sum(i.exitDataList?.map(m => m.sum))) ? sum(i.exitDataList?.map(m => m.sum)) : null,
            i.exitDataList,
          ]),
          markArea: {
            data: lossMarkArea(processLossViewVoToIns, activeKey),
          },
        },
        {
          name: '损耗（超限）',
          type: 'bar',
          stack: 'Ad',
          emphasis: {
            focus: 'series',
          },
          z: 0,
          itemStyle: biasItemSty('rgba(255, 77, 79, 0.4)', 'rgb(255, 77, 79)'),
          data: processLossViewVoToIns.map(m => {
            return {
              value: [m.ts, m.lossDataVo?.ifExceed ? m.lossDataVo?.loss : null],
              itemStyle: biasItemSty('rgba(255, 77, 79, 0.4)', 'rgb(255, 77, 79)'),
            };
          }),
          markArea: {
            data: lossMarkArea(processLossViewVoToIns, activeKey),
          },
        },
        {
          name: '损耗（正常）',
          type: 'bar',
          stack: 'Ad',
          emphasis: {
            focus: 'series',
          },
          z: 0,
          data: processLossViewVoToIns.map(m => {
            return {
              value: [m.ts, !m.lossDataVo?.ifExceed ? m.lossDataVo?.loss : null],
              itemStyle: biasItemSty('rgba(255,203,71,0.4)', 'rgb(255,203,71)'),
            };
          }),
          itemStyle: biasItemSty('rgba(255,203,71,0.4)', 'rgb(255,203,71)'),
          markArea: {
            data: lossMarkArea(processLossViewVoToIns, activeKey),
          },
        },
      ],
    };
  }
};

// ------------------------下面是弹窗详情的损耗

// tooltip 通用配置
export const getLossModalTooltip = ({ unit = '' }: { unit?: string }) => ({
  trigger: 'axis',
  backgroundColor: 'rgba(0,0,0,0.8)',
  borderColor: 'transparent',
  textStyle: {
    color: '#fff',
  },
  formatter(items: any) {
    const { axisValue } = items[0];
    // 根据选择的颗粒度，展示不同的tooltip
    let str: string = dayjs(axisValue, 'x').format('MM-DD[\n]HH:mm');
    items.forEach((item: any) => {
      const { seriesName, data, marker } = item;
      const value = !isNil(data[1]) ? `${data[1].toFixed(2)}${unit}` : '--';
      str += `<br> ${marker}${seriesName}: ${value}`;
    });
    return str;
  },
});

export const getLossModalNormalAxis = () => {
  return [
    {
      ...xAxis,
      type: 'time',
      // minInterval: 60 * 1000,
      // axisLabel: {
      //   formatter: function (e: number) {
      //     return dayjs(e, 'x').format('MM-DD[\n]HH:mm');
      //   },
      // },
    },
  ];
};

export const getLossDetailBasicChartOption = (unit: string) => {
  const colors = ['#1890ff', '#4fc7ec'];
  return {
    backgroundColor: window.getComputedStyle(document.documentElement).getPropertyValue('--component-background'),
    grid: {
      left: 80,
      right: 50,
      top: 55,
      bottom: 80,
    },
    dataZoom: [
      {
        type: 'slider',
        bottom: 15,
        height: 20,
        backgroundColor: 'rgba(226,227,229,0.5)',
        fillerColor: 'rgba(142,177,224,0.3)',
        textStyle: {
          fontSize: 10,
        },
        // startValue: dayjs().startOf('d').format('MM-DD[\n]HH:mm'),
      },
    ],
    color: colors,
    tooltip: getLossModalTooltip({
      unit,
    }),
    xAxis: getLossModalNormalAxis(),
    yAxis: {
      type: 'value',
      name: `${unit}`,
      nameTextStyle: {
        fontSize: 14,
        align: 'right',
      },
      nameGap: 30,
      splitLine: {
        lineStyle: {
          color: '#FFFFFF30',
        },
      },
    },
  };
};

// 获取图例
export const getLossModalLegend = () => {
  return {
    show: true,
    right: 20,
    top: 10,
    textStyle: {
      color: '#AFBCC4',
    },
    data: [
      {
        name: `输入`,
        icon: 'rect',
      },
      {
        name: `输出`,
        icon: 'rect',
      },
    ],
  };
};

export const getLossDetailChartOption = (
  energyMediumIndicatorId?: number,
  chartData?: V2EnergyCompareGetMediumIndicatorLossDataLossDetailPostResponse,
  mediumIndicatorDisplayUnitData?: MediumIndicatorDisplayUnit
) => {
  if (chartData && energyMediumIndicatorId && mediumIndicatorDisplayUnitData) {
    const unit = mediumIndicatorDisplayUnitData?.find(
      i => i.energyMediumIndicatorId === energyMediumIndicatorId
    )?.displayPhysicalUnitGeneralName;
    const option = unit && getLossDetailBasicChartOption(unit);
    const entryColors = ['#52A4FF', '#52A4FF'];
    const exitColors = ['#4FC448', '#4FC448'];

    return {
      ...option,
      legend: getLossModalLegend(),
      series: [
        {
          type: 'line',
          smooth: true,
          lineStyle: { normal: { width: 2 } },
          color: entryColors,
          areaStyle: {
            opacity: 1,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: '#51DBFF15',
              },
              {
                offset: 1,
                color: '#51A1FF05',
              },
            ]),
          },
          name: '输入',
          data: (chartData?.entryDataDetailList ?? []).map(a => {
            return [a.ts, a.sum];
          }),
        },
        {
          type: 'line',
          smooth: true,
          lineStyle: { normal: { width: 2 } },
          color: exitColors,
          areaStyle: {
            opacity: 1,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: '#47FF6615',
              },
              {
                offset: 1,
                color: '#5AD04205',
              },
            ]),
          },
          name: '输出',
          data: (chartData.exitDataDetailList ?? []).map(a => {
            return [a.ts, a.sum];
          }),
        },
      ],
    };
  }
};
